import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import type { Database } from "@/types/database.types";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { TaskDialog } from "@/components/project/TaskDialog";
import { useProjects } from "@/contexts/ProjectsContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Clock } from "lucide-react";
import { format, isToday, isTomorrow } from "date-fns";

type TaskWithDetailsRow = Database["public"]["Views"]["tasks_with_details"]["Row"];
type TaskPartialUpdate = Partial<Database["public"]["Tables"]["tasks"]["Row"]> & { id: string };

export function Backlogs() {
  const { user } = useAuth();
  const { projects, loading: projectsLoading } = useProjects();

  const [tasks, setTasks] = useState<TaskWithDetailsRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskWithDetailsRow | null>(null);

  const [selectedProjectId, setSelectedProjectId] = useState<string>("all");
  const [quickAddValue, setQuickAddValue] = useState("");
  const [quickAddLoading, setQuickAddLoading] = useState(false);
  const [quickAddProjectId, setQuickAddProjectId] = useState<string>("");

  useEffect(() => {
    if (projects.length > 0 && !quickAddProjectId) {
      setQuickAddProjectId(projects[0].project_id);
    }
  }, [projects, quickAddProjectId]);

  const fetchTasks = useCallback(async () => {
    if (projectsLoading || projects.length === 0) {
      setTasks([]);
      setLoading(false);
      return;
    }

    const projectIds = projects.map((p) => p.project_id);
    if (projectIds.length === 0) {
      setTasks([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from("tasks_with_details")
        .select("*")
        .in("project_id", projectIds)
        .eq("status", "todo")
        .order("created_at", { ascending: false });

      if (fetchError) throw fetchError;
      setTasks(data ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch tasks");
    } finally {
      setLoading(false);
    }
  }, [projectsLoading, projects]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleToggleComplete = async (task: TaskWithDetailsRow) => {
    if (!user || !task.id) return;

    const newStatus = "done";

    const { error: updateError } = await supabase
      .from("tasks")
      .update({
        status: newStatus,
        completed_at: new Date().toISOString(),
        completed_by: user.id,
        updated_at: new Date().toISOString(),
      })
      .eq("id", task.id);

    if (!updateError) {
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
    }
  };

  const handleTaskSuccess = useCallback(
    (_result?: TaskWithDetailsRow | TaskPartialUpdate) => {
      setTaskDialogOpen(false);
      setEditingTask(null);
      fetchTasks();
    },
    [fetchTasks],
  );

  const handleQuickAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const title = quickAddValue.trim();
    if (!title || projects.length === 0 || !user) return;

    setQuickAddLoading(true);
    try {
      const targetProjectId = quickAddProjectId || projects[0]?.project_id;
      if (!targetProjectId) return;
      await supabase.from("tasks").insert({
        title,
        status: "todo",
        project_id: targetProjectId,
        created_by: user.id,
        due_date: null,
      }).select().single();

      setQuickAddValue("");
      fetchTasks();
    } catch (err) {
      console.error("Quick add failed:", err);
    } finally {
      setQuickAddLoading(false);
    }
  };

  const filteredTasks = tasks.filter((task) => {
    if (selectedProjectId !== "all" && task.project_id !== selectedProjectId) return false;
    return true;
  });

  const formatDueDate = (dueDate: string | null) => {
    if (!dueDate) return null;
    const date = new Date(dueDate);
    if (isToday(date)) return "Today";
    if (isTomorrow(date)) return "Tomorrow";
    return format(date, "MMM d");
  };

  const getPriorityColor = (priority: string | null) => {
    switch (priority) {
      case "high": return "bg-red-500/20 text-red-600 dark:text-red-400";
      case "medium": return "bg-yellow-500/20 text-yellow-600 dark:text-yellow-400";
      case "low": return "bg-blue-500/20 text-blue-600 dark:text-blue-400";
      default: return "bg-muted text-muted-foreground";
    }
  };

  if (loading && tasks.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner className="size-8" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-destructive">{error}</div>
      </div>
    );
  }

  return (
    <>
    <div className="flex-1 flex flex-col overflow-hidden min-h-0">
      {/* Header */}
      <div className="border-b bg-background px-4 md:px-6 py-4 shrink-0">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold tracking-tight">Backlogs</h1>
            <div className="flex items-center gap-2">
              <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="All Projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {projects.map((p) => (
                    <SelectItem key={p.project_id} value={p.project_id}>
                      {p.project_title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={() => {
                  setEditingTask(null);
                  setTaskDialogOpen(true);
                }}
                size="sm"
                className="gap-1.5"
              >
                <Plus className="h-4 w-4" />
                Add Task
              </Button>
            </div>
          </div>
        </div>

        {/* Task List */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="w-full space-y-6">
            {filteredTasks.length === 0 ? (
              <div className="text-center py-12 text-sm text-muted-foreground">
                No tasks in backlog. Create one!
              </div>
            ) : (
              <div className="space-y-1.5">
                {filteredTasks.map((task) => (
                  <div
                    key={task.id}
                    className="group flex items-start gap-3 rounded-lg border bg-card p-3 hover:bg-accent/50 transition-colors"
                  >
                    <Checkbox
                      checked={false}
                      onCheckedChange={() => handleToggleComplete(task)}
                      className="mt-0.5 shrink-0"
                    />
                    <button
                      onClick={() => {
                        setEditingTask(task);
                        setTaskDialogOpen(true);
                      }}
                      className="flex-1 text-left min-w-0"
                    >
                      <p className="text-sm font-medium leading-tight">{task.title}</p>
                      <p className="text-xs text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
                        {task.project_name && (
                          <Link
                            to={`/project/${task.project_id}`}
                            className="hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {task.project_name}
                          </Link>
                        )}
                        {task.due_date && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDueDate(task.due_date)}
                          </span>
                        )}
                      </p>
                    </button>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {task.priority && (
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${getPriorityColor(task.priority)}`}>
                          {task.priority}
                        </span>
                      )}
                      {task.tags?.slice(0, 2).map((tag) => (
                        <span key={tag} className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Quick Add */}
        <div className="border-t bg-background p-4 shrink-0">
          <form onSubmit={handleQuickAdd} className="flex gap-2">
            <Select value={quickAddProjectId} onValueChange={setQuickAddProjectId}>
              <SelectTrigger className="w-[140px] h-10 shrink-0">
                <SelectValue placeholder="Select project" />
              </SelectTrigger>
              <SelectContent>
                {projects.map((p) => (
                  <SelectItem key={p.project_id} value={p.project_id}>
                    {p.project_title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Add a task to Backlog..."
              value={quickAddValue}
              onChange={(e) => setQuickAddValue(e.target.value)}
              disabled={quickAddLoading || projects.length === 0}
              className="h-10"
            />
            <Button
              type="submit"
              size="sm"
              disabled={!quickAddValue.trim() || quickAddLoading || projects.length === 0}
              className="h-10 shrink-0"
            >
              {quickAddLoading ? (
                <div className="size-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
            </Button>
          </form>
          {projects.length === 0 && (
            <p className="text-xs text-muted-foreground mt-1.5">
              Create a project first to add tasks.
            </p>
          )}
        </div>
      </div>

      <TaskDialog
        open={taskDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setTaskDialogOpen(false);
            setEditingTask(null);
          } else {
            setTaskDialogOpen(true);
          }
        }}
        mode={editingTask ? "edit" : "create"}
        task={editingTask}
        onSuccess={handleTaskSuccess}
      />
    </>
  );
}
